import { postReportComment, postReportPost } from "@/api/report";
import CommentReporter from "@/components/comment-reporter";
import ContentReporter from "@/components/content-reporter";
import { reportCommentOptions } from "@/constants";
import { useAuthContext } from "@/context/auth/useAuthContext";
import { useTranslations } from "@/lib/static-intl";
import { enqueueSnackbar } from "notistack";
import { useState } from "react";

const ContentOrCommentReporter = ({
  postData,
  isReportingPost,
  setIsReportingPost,
  isReportModalOpen,
  setIsReportModalOpen,
  commentToBeReported,
  setCommentToBeReported,
}) => {
  const t = useTranslations("user_profile");
  const apiT = useTranslations("api");
  const { user } = useAuthContext();

  const [isStepNext, setIsStepNext] = useState(false);
  const [submitBtnLoader, setSubmitBtnLoader] = useState(false);
  const [notesReportForm, setNotesReportForm] = useState({
    reportNotes: "",
    reportDescription: "",
    reportNotesFiles: [],
  });
  const [commentReportingOptions, setCommentReportingOptions] =
    useState(reportCommentOptions);

  const resetReportModal = () => {
    setNotesReportForm({
      reportNotes: "",
      reportDescription: "",
      reportNotesFiles: [],
    });
    setIsStepNext(false);
    setCommentToBeReported(null);
    setIsReportingPost(false);
    setIsReportModalOpen(false);
    setCommentReportingOptions((prev) =>
      prev.map((option) => ({ ...option, isSelected: false }))
    );
  };

  const toggleOption = (index) => {
    setCommentReportingOptions((prevOptions) =>
      prevOptions.map((option, i) =>
        i === index
          ? { ...option, isSelected: !option.isSelected }
          : { ...option, isSelected: false }
      )
    );
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length + notesReportForm.reportNotesFiles.length <= 3) {
      setNotesReportForm({
        ...notesReportForm,
        reportNotesFiles: [...notesReportForm.reportNotesFiles, ...files],
      });
    } else {
      enqueueSnackbar(t("upload_upto_3_images"), {
        variant: "error",
      });
    }
  };

  const removeFile = (index) => {
    const updatedFiles = notesReportForm.reportNotesFiles.filter(
      (_, i) => i !== index
    );
    setNotesReportForm({ ...notesReportForm, reportNotesFiles: updatedFiles });
  };

  const createCommentReport = async () => {
    try {
      setSubmitBtnLoader(true);
      const payload = {
        reporterId: user?.userId,
        commentId: commentToBeReported?.id,
        category: commentReportingOptions.filter(
          (option) => option.isSelected
        )[0]?.option,
      };
      const apiResp = await postReportComment(payload);
      if (apiResp?.status === 201) {
        enqueueSnackbar(t("comment_reported_successfully"), {
          variant: "success",
        });
        setCommentToBeReported(null);
        setIsReportModalOpen(false);
      }
    } catch (error) {
      enqueueSnackbar(
        error?.response?.data?.message ||
          error?.message ||
          apiT("error message"),
        { variant: "error" }
      );
    } finally {
      setSubmitBtnLoader(false);
      setCommentToBeReported(null);
      setIsReportModalOpen(false);
    }
  };

  const submitNotesReport = async () => {
    setSubmitBtnLoader(true);
    const payload = {
      reporterId: user?.userId,
      postId: postData?.id,
      category:
        notesReportForm?.reportNotes.length > 0
          ? `${
              commentReportingOptions.filter((option) => option.isSelected)[0]
                ?.option
            }; ${notesReportForm?.reportNotes}`
          : commentReportingOptions.filter((option) => option.isSelected)[0]
              ?.option,
      description: notesReportForm.reportDescription,
    };

    try {
      const apiResp = await postReportPost(payload);
      if (apiResp?.status === 201) {
        const createNewPostReportID = apiResp?.data?.id;

        if (notesReportForm.reportNotesFiles.length > 0) {
          notesReportForm?.reportNotesFiles?.forEach(async (file) => {
            const formData = new FormData();
            formData.append("file", file);
            formData.append("reportId", createNewPostReportID);
            formData.append("mediaType", file.type);
            await postReportMediaUpload(formData);
          });
        }

        setIsStepNext(false);
        setNotesReportForm({
          reportNotes: "",
          reportDescription: "",
          reportNotesFiles: [],
        });
        setIsReportingPost(false);
        setIsReportModalOpen(false);
      }
    } catch (error) {
      enqueueSnackbar(
        error?.response?.data?.message ||
          error?.message ||
          apiT("error message"),
        { variant: "error" }
      );
    } finally {
      setSubmitBtnLoader(false);
    }
  };

  if (isReportingPost && isReportModalOpen) {
    return (
      <ContentReporter
        setIsReportModalOpen={setIsReportModalOpen}
        commentReportingOptions={commentReportingOptions}
        isStepNext={isStepNext}
        toggleOption={toggleOption}
        setNotesReportForm={setNotesReportForm}
        notesReportForm={notesReportForm}
        handleFileChange={handleFileChange}
        removeFile={removeFile}
        setIsStepNext={setIsStepNext}
        submitNotesReport={submitNotesReport}
        submitBtnLoader={submitBtnLoader}
        resetReportModal={resetReportModal}
      />
    );
  }

  if (isReportModalOpen) {
    return (
      <CommentReporter
        setIsReportModalOpen={setIsReportModalOpen}
        commentReportingOptions={commentReportingOptions}
        toggleOption={toggleOption}
        createCommentReport={createCommentReport}
        submitBtnLoader={submitBtnLoader}
        resetReportModal={resetReportModal}
      />
    );
  }

  return null;
};

export default ContentOrCommentReporter;
